import React, { useMemo } from 'react';
import {
  Button,
  Heading,
  List,
  SearchInput,
} from '@alefeducation/ui-elements-library-core';
import styled from 'styled-components';
import { PlusCircleSolid } from '@alefeducation/ui-elements-library-icons';
import { useHistory } from 'react-router-dom';
import useMembersPage from './useMembersPage';
import { getMembersColumns } from '../../components/Members/MembersRows';
import { Member } from '../../apollo/queries';
import DataGrid from '../../components/DataGrid/DataGrid';
import { NoItemsView } from '../../components/Organisations';
import {
  ADD_MEMBER,
  MEMBERS,
  OOPS_SOMETHING_WENT_WRONG_MESSAGE,
  SEARCH_BOX_PLACEHOLDER,
} from '../../utils/constants';
import { TableContainer } from '../../components/DataGrid/StyledTable';

const StyledMemberContainer = styled(List)`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledTableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: ${({ theme: { space } }: any) => `${space[24]}px`};
`;

const StyledAddMemberButtonContainer = styled.div`
  justify-content: flex-end;
`;

const StyledContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
  margin-right: 25px;
`;

const Members = () => {
  const history = useHistory();

  const {
    search,
    isViewOnlyRole,
    onSearchChange,
    setCurrentPage,
    setSearch,
    refetch,
    userData: { currentPage, loading, error, users, totalCount, pageSize },
    onPageSizeChange,
    isComponentMounted,
  } = useMembersPage({ history });

  const columns = useMemo(
    () => getMembersColumns({ refetch, isViewOnlyRole }),
    [refetch, isViewOnlyRole],
  );

  const handleRowClick = (member: Member) => {
    history.push(`/members/${member.id}/edit`, {
      pageNumber: currentPage,
    });
  };

  const showError = error && isComponentMounted;

  return (
    <React.Fragment>
      <StyledContainer>
        <SearchInput
          width="310px"
          value={search}
          placeholder={SEARCH_BOX_PLACEHOLDER}
          onChange={(e) => onSearchChange(e.target.value)}
          onClear={() => setSearch('')}
          onSearch={() => {}}
        />
      </StyledContainer>

      <StyledMemberContainer>
        <StyledTableHeader>
          <Heading variant="heading-medium">{MEMBERS}</Heading>
          {!isViewOnlyRole && (
            <StyledAddMemberButtonContainer>
              <Button
                icon={<PlusCircleSolid />}
                data-testid="add-member-button"
                onClick={() =>
                  history.push('/members/add-member', {
                    pageNumber: currentPage,
                  })
                }
              >
                {ADD_MEMBER}
              </Button>
            </StyledAddMemberButtonContainer>
          )}
        </StyledTableHeader>

        <TableContainer>
          {showError ? (
            <NoItemsView title={OOPS_SOMETHING_WENT_WRONG_MESSAGE} />
          ) : (
            <DataGrid<Member>
              dataQa="members"
              data={users}
              columns={columns}
              isDataLoading={loading}
              allowPagination
              page={currentPage}
              size={pageSize}
              pageCount={totalCount}
              onPageChange={setCurrentPage}
              onPageSizeChange={onPageSizeChange}
              onRowClick={handleRowClick}
            />
          )}
        </TableContainer>
      </StyledMemberContainer>

      <div id="members-page-toast" />
      <div id="edit-status-toast" />
    </React.Fragment>
  );
};

export default Members;
